<!--
  RuleSetBuilderIframe.vue

  Reusable wrapper around the performx RuleSetBuilder, embedded via <iframe> for
  full CSS/JS isolation (host is Vue 3 + Vuetify 3; this app is Vue 2 + Vuetify 2).

  The iframe host is served at VUE_APP_RULE_BUILDER_URL (see .env.example) and
  speaks a postMessage protocol, all prefixed with "rsb:". This component owns
  the parent side of that bridge.

  Canonical protocol spec (message set, versioning/handshake, origin model, the
  validation-gate contract, rsb:error) is owned by the builder repo:
    performx-ruleset-builder/docs/IFRAME_MESSAGING_CONTRACT.md
  The protocol version + message-type constants live in ./messagingContract.js and
  must stay in sync with that doc and the builder's messagingContract.ts.

  allow="clipboard-write" delegates the Permissions Policy to the builder. Its
  default allowlist is `self`, so a cross-origin iframe does not inherit it and
  navigator.clipboard.writeText() rejects with NotAllowedError inside the builder
  — its copy buttons work standalone and fail here. `sandbox` and `allow` are
  independent: the sandbox list below governs what the frame may do, this governs
  which browser features it is granted.

  Usage:
    <RuleSetBuilderIframe
      ref="ruleBuilder"
      :tenant-id="tenantId"
      :rule-set-id="ruleSetId"
      :name="programName"
      :description="programDescription"
      :valid-from="programStartDateIso"
      :valid-to="programEndDateIso"
      @ready="onReady"
      @dirty-change="isDirty = $event"
      @validation-change="onValidation"
    />

    // Capture the current rule set without saving (e.g. a draft snapshot):
    const ruleSet = await this.$refs.ruleBuilder.getState();

    // Validate + persist on the wizard's "Next", then link the id to the program:
    const { ok, ruleSetId, validation } = await this.$refs.ruleBuilder.save();

  The builder has no Publish button in embedded mode, so save() is how the parent
  drives validate+persist: it round-trips rsb:save -> rsb:save-result and resolves
  with the builder's SaveResult ({ ok, ruleSetId?, ruleSet?, validation?, error? }).
  Inbound builder emits (save/cancel/delete/validation-change/dirty-change) are still
  forwarded as native Vue events for the parent to observe.
-->
<template>
  <div class="rule-set-builder-iframe" :class="{ 'fill-height-mode': fillHeight }">
    <v-progress-linear v-if="!ready && !error" indeterminate color="brandPrimary" class="mb-0" />

    <!-- The error message is surfaced via the global snackbar (showError); this is
         just the inline recovery affordance so the user can re-attempt a failed load. -->
    <div v-if="error" class="pa-4 text-center">
      <v-btn small outlined color="brandPrimary" @click="retry"> Retry </v-btn>
    </div>

    <iframe
      v-show="!error"
      ref="iframe"
      :key="iframeKey"
      :src="embedUrl"
      title="RuleSet Builder"
      frameBorder="0"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      allow="clipboard-write"
      :style="iframeStyle"
      @load="onIframeLoad"
    ></iframe>
  </div>
</template>

<script>
import activeClientMixin from "@/mixins/activeClientMixin";
import hostConfig from "@/config/hostResolver";
import {
  PROTOCOL_VERSION,
  isMajorCompatible,
  HOST_TO_BUILDER,
  BUILDER_TO_HOST,
} from "./messagingContract";

// Fallback host if the env var is unset. See .env.example → VUE_APP_RULE_BUILDER_URL.
const DEFAULT_EMBED_URL = "https://pxui.performx.dev/rule-builder-embed/";

// Min iframe height (px). rsb:resize messages grow it past this.
const MIN_HEIGHT = 400;

// Fail with an error + retry if the iframe never sends rsb:ready.
const READY_TIMEOUT_MS = 10000;

// Reject a pending getState() if the iframe never answers with rsb:state.
const STATE_TIMEOUT_MS = 5000;

// Reject a pending save() if the iframe never answers with rsb:save-result. Save
// runs validate + persist + re-fetch on the builder (several API round-trips), so
// it needs a much longer budget than a plain state read.
const SAVE_TIMEOUT_MS = 35000;

export default {
  name: "RuleSetBuilderIframe",
  mixins: [activeClientMixin], // provides getClientField() + globalClientId/userProfile/clients
  props: {
    // The DaaS tenant. Optional override — when omitted, the wrapper resolves the
    // active client's `informTenantId` (via activeClientMixin) and sends that in rsb:init.
    tenantId: { type: String, default: null },
    // Existing rule set to load (edit mode). Null/omitted = create mode.
    ruleSetId: { type: String, default: null },
    readonly: { type: Boolean, default: false },
    // RuleSetBuilderCssVars passthrough (brand theming). Plain object only.
    themeOverrides: { type: Object, default: null },
    // Name/description are hidden inside the builder in embedded mode, so the
    // parent supplies them via rsb:init / rsb:set-metadata.
    name: { type: String, default: null },
    description: { type: String, default: null },
    // Rule set validity window, mapped onto the builder's validFrom/validTo (the
    // "Valid From"/"Valid To" dates). Like name/description these are set by the
    // parent (e.g. a program builder forwarding its start/end dates) and pushed via
    // rsb:init / rsb:set-metadata. ISO 8601 strings — the builder parses them with
    // `new Date(...)`.
    validFrom: { type: String, default: null },
    validTo: { type: String, default: null },
    // One-shot seed for a brand-new RuleSet's Global Filters — see GlobalFilterSeed
    // in performx-ruleset-builder. Sent ONLY on rsb:init (create-mode bootstrap);
    // deliberately has no watcher and is NOT re-pushed via sendPropsUpdate()/
    // rsb:set-metadata on a later prop change (e.g. the parent program's start/end
    // dates changing after this step has already initialized) — there's no way to
    // tell an unedited seed value apart from a real user edit once seeded, so
    // re-pushing would risk clobbering a genuine edit. Unrelated to validFrom/
    // validTo (the RuleSet's own scheduling window) — do not conflate.
    initialGlobalFilters: { type: Array, default: null },
    // Output labels a rule may mark durable — the builder's "Keep after the run"
    // switch, which sets OutputLabelType: 'Known' so the nodes are written under
    // the bare label, survive run cleanup, and can be read outside the rules
    // engine. Which labels are shared contracts belongs to us, not the builder:
    // `Payout` is one because the Engage payout flow reads (:Payout) nodes by
    // that exact plain name, and a rule left as the default `Calculated` writes
    // `#Payout` instead — which nothing reads, forever, with no error anywhere.
    // `Metric` is one for the same reason: daas-engage-integration's
    // MetricController reads (:Metric) nodes by that bare name.
    // Add to this list as other contract labels appear. Pass [] to hide the
    // switch entirely.
    durableOutputLabels: { type: Array, default: () => ["Payout", "Metric"] },
    // Named values we supply for the rule author to reference, shown read-only
    // with a copy button in the builder's reference pane. The builder never
    // interprets or substitutes them — it just displays what we hand it.
    //
    // This exists because the values a payout rule has to be scoped by are minted
    // by us and are opaque to the author. Each entry is { name, value }.
    constants: { type: Array, default: () => [] },
    // NOTE: the Rules API base URL is not a prop, but the parent DOES forward it on
    // rsb:init (see rulesProxyUrl / sendInit). The builder's calls route through the
    // whistle-side programs v3 proxy (/v3/rules-proxy) rather than hitting DaaS
    // directly, so the parent owns the backend address for this environment.
    // Fill the parent's height (builder scrolls internally) instead of auto-growing
    // the iframe to its content height via rsb:resize. Needs a height-sized parent.
    fillHeight: { type: Boolean, default: false },
  },
  data() {
    return {
      ready: false, // rsb:ready received
      iframeLoaded: false, // iframe load event fired
      error: null, // user-facing error string
      iframeHeight: MIN_HEIGHT,
      iframeKey: 0, // bump to force a remount on retry
      readyTimeoutId: null,
      // Buffered setMetadata() calls made before the iframe is ready.
      pendingMetadata: null,
      // FIFO queue of { resolve, reject, timeoutId } for in-flight getState() calls.
      stateResolvers: [],
      // FIFO queue of { resolve, reject, timeoutId } for in-flight save() calls.
      saveResolvers: [],
      // tenantId actually sent in rsb:init (prop override, else informTenantId).
      resolvedTenantId: null,
      // Promise for the (possibly async) tenant resolution kicked off on mount.
      tenantPromise: null,
    };
  },
  computed: {
    embedUrl() {
      return process.env.VUE_APP_RULE_BUILDER_URL || DEFAULT_EMBED_URL;
    },
    // fillHeight: flex-fill the parent (builder scrolls inside). Otherwise size to
    // the content height reported via rsb:resize.
    iframeStyle() {
      const base = { width: "100%", display: "block", border: "0" };
      return this.fillHeight
        ? { ...base, flex: "1 1 auto" }
        : { ...base, height: this.iframeHeight + "px" };
    },
    // Origin we accept messages from and target our postMessages to.
    embedOrigin() {
      try {
        return new URL(this.embedUrl).origin;
      } catch (e) {
        return "";
      }
    },
    // Auth0 access token from the store; forwarded on init and on change.
    authToken() {
      return this.$store.state.token;
    },
    // Rules API base URL forwarded on rsb:init. Points at the inform service's rules
    // reverse proxy (/v3/rules-proxy), which authenticates the whistle token, resolves
    // the target client's DaaS tenant, mints a DaaS-audience token, and forwards to
    // daas-rules. Bare base with NO /rules suffix — the builder appends /rules/...
    // itself. Mirrors the inform base-URL resolution in InformService.js.
    rulesProxyUrl() {
      const informBase =
        process.env.VUE_APP_INFORM_URL ?? `${hostConfig.apiBaseUrl}/inform-service`;
      return `${informBase}/v3/rules-proxy`;
    },
  },
  watch: {
    // Auth0 auto-refresh replaces the token (store setToken → autoRefreshToken).
    // Push the new one so the builder's DaaS calls don't 401.
    authToken(newToken) {
      if (this.ready && newToken) {
        this.postToIframe(HOST_TO_BUILDER.UPDATE_TOKEN, { payload: { authToken: newToken } });
      }
    },
    readonly() {
      this.sendPropsUpdate();
    },
    ruleSetId() {
      this.sendPropsUpdate();
    },
    // Active client switched → re-resolve informTenantId and re-init the builder.
    async globalClientId() {
      this.tenantPromise = this.resolveTenant();
      await this.tenantPromise;
      if (this.ready) this.sendInit();
    },
    // If name/description change after init (e.g. user went Back, edited, returned),
    // push them into the builder. Buffers automatically if not yet ready.
    name() {
      this.pushMetadata();
    },
    description() {
      this.pushMetadata();
    },
    validFrom() {
      this.pushMetadata();
    },
    validTo() {
      this.pushMetadata();
    },
  },
  mounted() {
    window.addEventListener("message", this.onMessage);
    // Resolve the tenant early, in parallel with the iframe load, so it's ready
    // by the time rsb:ready fires. onReady awaits this before sending rsb:init.
    this.tenantPromise = this.resolveTenant();
    this.startReadyTimeout();
    // The iframe loads from :src automatically; we wait for rsb:ready before init.
  },
  // Vue 2.7 Options API: the real teardown hook is beforeDestroy. (This codebase
  // uses beforeUnmount widely, but that is a Vue 3 name and never fires in Vue 2,
  // so it would leak the global message listener + timers here. The lint rule
  // below is a mis-applied Vue 3 rule; beforeDestroy is correct for this runtime.)
  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    window.removeEventListener("message", this.onMessage);
    this.clearReadyTimeout();
    this.rejectPendingState(new Error("RuleSetBuilderIframe destroyed"));
    this.rejectPendingSave(new Error("RuleSetBuilderIframe destroyed"));
  },
  methods: {
    // ─── Outbound: parent → iframe ──────────────────────────────────────────
    postToIframe(type, extra) {
      const frame = this.$refs.iframe;
      if (!frame || !frame.contentWindow || !this.embedOrigin) return;
      frame.contentWindow.postMessage({ type, ...(extra || {}) }, this.embedOrigin);
    },
    // Resolve the tenant to send in rsb:init: explicit prop override wins, else the
    // active client's informTenantId (activeClientMixin, keyed on globalClientId).
    // Fresh, like the Program Management card: the cached dropdown can trail an Inform save.
    async resolveTenant() {
      if (this.tenantId) {
        this.resolvedTenantId = this.tenantId;
        return;
      }
      try {
        const { informTenantId } = await this.getClientFields(
          ["informTenantId"],
          this.globalClientId,
          { fresh: true }
        );
        this.resolvedTenantId = informTenantId;
      } catch (e) {
        this.resolvedTenantId = null;
      }
      if (!this.resolvedTenantId) {
        // eslint-disable-next-line no-console
        console.warn(
          "[RuleSetBuilderIframe] no tenantId — informTenantId is unset for the active client"
        );
      }
    },
    sendInit() {
      this.postToIframe(HOST_TO_BUILDER.INIT, {
        // Stamp our protocol version so the builder can gate on it (handshake only).
        protocolVersion: PROTOCOL_VERSION,
        payload: {
          tenantId: this.resolvedTenantId,
          // Route the builder's rules calls through the inform-side rules proxy.
          // whistle's Auth0 token has aud "whistlev1", which DaaS rejects; the proxy
          // validates this token and forwards to daas-rules with a DaaS-audience token,
          // so no token exchange is needed here (see RULES_PROXY_AUTH_PLAN).
          apiBaseUrl: this.rulesProxyUrl,
          // Tell the proxy which client the request acts on so it can authorize the
          // caller and resolve the DaaS tenant. This is the active (global) client,
          // which may differ from the user's home client for cross-client admins.
          requestHeaders: { "x-target-client-id": String(this.globalClientId) },
          authToken: this.authToken,
          ruleSetId: this.ruleSetId || undefined,
          readonly: this.readonly,
          themeOverrides: this.themeOverrides || undefined,
          name: this.name || undefined,
          description: this.description || undefined,
          validFrom: this.validFrom || undefined,
          validTo: this.validTo || undefined,
          initialGlobalFilters: this.initialGlobalFilters || undefined,
          durableOutputLabels: this.durableOutputLabels,
          constants: this.constants,
        },
      });
    },
    sendPropsUpdate() {
      if (!this.ready) return;
      this.postToIframe(HOST_TO_BUILDER.UPDATE_PROPS, {
        payload: { readonly: this.readonly, ruleSetId: this.ruleSetId || undefined },
      });
    },
    pushMetadata() {
      const payload = {};
      if (this.name != null) payload.name = this.name;
      if (this.description != null) payload.description = this.description;
      if (this.validFrom != null) payload.validFrom = this.validFrom;
      if (this.validTo != null) payload.validTo = this.validTo;
      if (!Object.keys(payload).length) return;
      this.setMetadata(payload);
    },

    // ─── Public API (called by the parent wizard) ───────────────────────────
    // Ask the builder to serialize its current state. Resolves with a RuleSet.
    getState() {
      return new Promise((resolve, reject) => {
        if (!this.ready) {
          reject(new Error("RuleSetBuilder is not ready yet"));
          return;
        }
        const timeoutId = setTimeout(() => {
          this.stateResolvers = this.stateResolvers.filter((r) => r.timeoutId !== timeoutId);
          reject(new Error("Timed out waiting for rsb:state"));
        }, STATE_TIMEOUT_MS);
        this.stateResolvers.push({ resolve, reject, timeoutId });
        this.postToIframe(HOST_TO_BUILDER.GET_STATE);
      });
    },
    // Ask the builder to validate + persist the current rule set (it routes the save
    // through the rules proxy → DaaS). Resolves with the builder's SaveResult:
    // { ok, ruleSetId?, ruleSet?, validation?, error? }. A validation or save failure
    // resolves with ok:false (NOT a rejection) so callers can branch on the outcome;
    // the promise only rejects if the builder isn't ready or never answers.
    save() {
      return new Promise((resolve, reject) => {
        if (!this.ready) {
          reject(new Error("RuleSetBuilder is not ready yet"));
          return;
        }
        const timeoutId = setTimeout(() => {
          this.saveResolvers = this.saveResolvers.filter((r) => r.timeoutId !== timeoutId);
          reject(new Error("Timed out waiting for rsb:save-result"));
        }, SAVE_TIMEOUT_MS);
        this.saveResolvers.push({ resolve, reject, timeoutId });
        this.postToIframe(HOST_TO_BUILDER.SAVE);
      });
    },
    // Set metadata (name/description/validFrom/validTo) on the builder. Buffers if
    // called before ready.
    setMetadata(payload) {
      if (!this.ready) {
        this.pendingMetadata = { ...(this.pendingMetadata || {}), ...payload };
        return;
      }
      this.postToIframe(HOST_TO_BUILDER.SET_METADATA, { payload });
    },

    // ─── Inbound: iframe → parent ───────────────────────────────────────────
    onMessage(event) {
      // Only trust the embed origin, and only our own protocol.
      if (!this.embedOrigin || event.origin !== this.embedOrigin) return;
      const data = event.data;
      if (!data || typeof data.type !== "string" || !data.type.startsWith("rsb:")) return;

      switch (data.type) {
        case BUILDER_TO_HOST.READY:
          this.onReady(data.protocolVersion);
          break;
        case BUILDER_TO_HOST.STATE:
          this.resolveNextState(data.payload);
          break;
        case BUILDER_TO_HOST.SAVE_RESULT:
          this.resolveNextSave(data.payload);
          break;
        case BUILDER_TO_HOST.EVENT:
          this.onBuilderEvent(data.name, data.payload);
          break;
        case BUILDER_TO_HOST.RESIZE:
          if (data.payload && typeof data.payload.height === "number") {
            this.iframeHeight = Math.max(MIN_HEIGHT, data.payload.height);
          }
          break;
        case BUILDER_TO_HOST.ERROR:
          this.notifyError((data.payload && data.payload.message) || "RuleSet Builder error");
          this.$emit("error", data.payload);
          break;
      }
    },
    async onReady(builderVersion) {
      // Handshake version gate: the builder announces its protocolVersion on ready.
      // An incompatible major (or a missing version) fails loudly and we refuse to
      // init, so we never drive an incompatible builder.
      if (!isMajorCompatible(builderVersion)) {
        this.clearReadyTimeout();
        const message =
          `RuleSet Builder protocol version ${builderVersion || "(none)"} is incompatible ` +
          `with this app (${PROTOCOL_VERSION}). The builder must be updated to a matching ` +
          `major version.`;
        // eslint-disable-next-line no-console
        console.error("[RuleSetBuilderIframe]", message);
        this.notifyError(message);
        return;
      }
      this.ready = true;
      this.clearReadyTimeout();
      // Ensure the tenant is resolved before init (usually already done since it
      // started on mount, in parallel with the iframe load).
      await this.tenantPromise;
      this.sendInit();
      // Flush any metadata buffered before the builder was ready.
      if (this.pendingMetadata) {
        this.postToIframe(HOST_TO_BUILDER.SET_METADATA, { payload: this.pendingMetadata });
        this.pendingMetadata = null;
      }
      this.$emit("ready", this.resolvedTenantId);
    },
    // Forward builder emits up as native Vue events.
    // STUB: parent decides what to do with each (persist, navigate, toast, …).
    onBuilderEvent(name, payload) {
      switch (name) {
        case "save":
          // TODO: parent persists the returned RuleSet.
          this.$emit("save", payload);
          break;
        case "cancel":
          // TODO: parent closes/returns.
          this.$emit("cancel");
          break;
        case "delete":
          // TODO: parent removes the rule set (payload = ruleSetId).
          this.$emit("delete", payload);
          break;
        case "validation-change":
          // Relayed for host observability. Enforcement is the save() round-trip
          // (ok:false blocks advancing/closing); the host doesn't gate its button on
          // this, since the relayed result goes stale as soon as the user edits.
          this.$emit("validation-change", payload);
          break;
        case "dirty-change":
          // Relayed for host observability (unsaved-changes tracking).
          this.$emit("dirty-change", payload);
          break;
        default:
          // eslint-disable-next-line no-console
          console.warn("[RuleSetBuilderIframe] unhandled rsb:event", name);
      }
    },

    // ─── getState() plumbing ────────────────────────────────────────────────
    resolveNextState(payload) {
      const pending = this.stateResolvers.shift();
      if (!pending) return;
      clearTimeout(pending.timeoutId);
      pending.resolve(payload);
    },
    rejectPendingState(err) {
      this.stateResolvers.forEach((r) => {
        clearTimeout(r.timeoutId);
        r.reject(err);
      });
      this.stateResolvers = [];
    },
    resolveNextSave(payload) {
      const pending = this.saveResolvers.shift();
      if (!pending) return;
      clearTimeout(pending.timeoutId);
      pending.resolve(payload);
    },
    rejectPendingSave(err) {
      this.saveResolvers.forEach((r) => {
        clearTimeout(r.timeoutId);
        r.reject(err);
      });
      this.saveResolvers = [];
    },

    // ─── Loading / error lifecycle ──────────────────────────────────────────
    onIframeLoad() {
      this.iframeLoaded = true;
    },
    // Surface an error via the app's global snackbar, and set an internal flag that
    // hides the iframe and shows the inline Retry affordance.
    notifyError(message) {
      this.error = message;
      this.$store.dispatch("showError", { title: "RuleSet Builder", message });
    },
    startReadyTimeout() {
      this.clearReadyTimeout();
      this.readyTimeoutId = setTimeout(() => {
        if (!this.ready) {
          this.notifyError("The RuleSet Builder didn't respond. Please retry.");
        }
      }, READY_TIMEOUT_MS);
    },
    clearReadyTimeout() {
      if (this.readyTimeoutId) {
        clearTimeout(this.readyTimeoutId);
        this.readyTimeoutId = null;
      }
    },
    retry() {
      this.error = null;
      this.ready = false;
      this.iframeLoaded = false;
      this.iframeHeight = MIN_HEIGHT;
      this.rejectPendingState(new Error("Retrying RuleSetBuilder"));
      this.rejectPendingSave(new Error("Retrying RuleSetBuilder"));
      this.iframeKey += 1; // force a fresh iframe (re-fires load → rsb:ready)
      this.startReadyTimeout();
    },
  },
};
</script>

<style scoped>
.rule-set-builder-iframe {
  width: 100%;
}
/* fillHeight: fill the parent's height; the iframe flexes to fill remaining space. */
.rule-set-builder-iframe.fill-height-mode {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
